import { supabase } from '@/lib/supabase';
import type { Cart, CartServiceResponse } from './cart.types';

export class CartCrudService {

  async getCurrentCart(userId?: string, sessionId?: string): Promise<Cart | null> {
    try {
      // First, check and expire any old carts (older than 30 minutes)
      const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000).toISOString();
      
      if (userId) {
        await supabase
          .from('carts')
          .update({ status: 'expired', updated_at: new Date().toISOString() })
          .eq('user_id', userId)
          .eq('status', 'active')
          .lt('created_at', thirtyMinutesAgo);
      }

      let query = supabase
        .from('carts')
        .select(`
          *,
          restaurant:restaurants(id, name, image_url),
          items:cart_items(
            *,
            product:products(
              id,
              name,
              original_price,
              image_url,
              restaurant_id,
              stock:product_stock(date, current_stock)
            ),
            offer:product_offers(id, offer_type, discount_rate, discounted_price)
          )
        `)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1);

      if (userId) {
        query = query.eq('user_id', userId);
      } else if (sessionId) {
        query = query.eq('session_id', sessionId);
      } else {
        return null;
      }

      const { data, error } = await query.maybeSingle();
      
      if (error) {
        console.error('Error fetching cart:', error);
        return null;
      }
      
      if (!data) {
        // No active cart found, which is normal for new users
        return null;
      }

      // If cart has no items, expire it automatically
      if (!data.items || data.items.length === 0) {
        await supabase
          .from('carts')
          .update({ status: 'expired', updated_at: new Date().toISOString() })
          .eq('id', data.id);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error getting current cart:', error);
      return null;
    }
  }

  async createCart(restaurantId: string, userId?: string, sessionId?: string): Promise<Cart | null> {
    try {
      const cartData: any = {
        restaurant_id: restaurantId,
        status: 'active',
        item_count: 0,
        expires_at: new Date(Date.now() + 30 * 60 * 1000).toISOString() // 30 minutes
      };

      if (userId) {
        cartData.user_id = userId;
      } else if (sessionId) {
        cartData.session_id = sessionId;
      } else {
        throw new Error('Either userId or sessionId must be provided');
      }

      const { data, error } = await supabase
        .from('carts')
        .insert(cartData)
        .select()
        .single();

      if (error) {
        console.error('Cart creation error details:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error creating cart:', error);
      return null;
    }
  }

  async clearCart(cartId: string): Promise<CartServiceResponse> {
    try {
      // Delete all cart items
      const { error: itemsError } = await supabase
        .from('cart_items')
        .delete()
        .eq('cart_id', cartId);

      if (itemsError) {
        return { success: false, error: '장바구니 비우기에 실패했습니다.' };
      }

      // Update cart status and item_count
      const { error: cartError } = await supabase
        .from('carts')
        .update({ 
          status: 'expired',
          item_count: 0,
          updated_at: new Date().toISOString()
        })
        .eq('id', cartId);

      if (cartError) {
        console.error('Error updating cart status:', cartError);
      }

      return { success: true };
    } catch (error) {
      console.error('Error clearing cart:', error);
      return { success: false, error: '알 수 없는 오류가 발생했습니다.' };
    }
  }

  /**
   * 다른 매장 상품 추가 시 기존 장바구니 초기화
   */
  async clearCartForRestaurantChange(userId?: string, sessionId?: string): Promise<CartServiceResponse> {
    try {
      const cart = await this.getCurrentCart(userId, sessionId);
      
      if (cart) {
        return await this.clearCart(cart.id);
      }

      return { success: true };
    } catch (error) {
      console.error('Error clearing cart for restaurant change:', error);
      return { success: false, error: '장바구니 초기화 중 오류가 발생했습니다.' };
    }
  }

  /**
   * 결제 실패 시 장바구니 상태 복원
   * @param cartId 장바구니 ID
   */
  async restoreCartOnPaymentFailure(cartId: string): Promise<CartServiceResponse> {
    try {
      console.log('Restoring cart after payment failure:', cartId);
      
      // 장바구니를 다시 활성 상태로 변경
      const { error: cartError } = await supabase
        .from('carts')
        .update({
          status: 'active',
          updated_at: new Date().toISOString()
        })
        .eq('id', cartId);
      
      if (cartError) {
        console.error('Error reactivating cart:', cartError);
        return { 
          success: false, 
          error: '장바구니 복원 중 오류가 발생했습니다.' 
        };
      }

      console.log('Cart restoration completed successfully');
      return { success: true };
    } catch (error) {
      console.error('Error during cart restoration:', error);
      return { 
        success: false, 
        error: '장바구니 복원 중 예상치 못한 오류가 발생했습니다.' 
      };
    }
  }

  /**
   * 장바구니 아이템 개수 업데이트
   */
  async updateCartItemCount(cartId: string): Promise<void> {
    try {
      const { data } = await supabase
        .from('cart_items')
        .select('product_id')
        .eq('cart_id', cartId);

      const uniqueCount = new Set(data?.map(item => item.product_id)).size;

      await supabase
        .from('carts')
        .update({ 
          item_count: uniqueCount,
          updated_at: new Date().toISOString()
        })
        .eq('id', cartId);
    } catch (error) {
      console.error('Error updating cart item count:', error);
    }
  }

  async getCartTotal(cartId: string): Promise<number> {
    try {
      const { data } = await supabase
        .from('cart_items')
        .select('quantity, unit_price')
        .eq('cart_id', cartId);

      if (!data) return 0;

      return data.reduce((total, item) => total + (item.quantity * item.unit_price), 0);
    } catch (error) {
      console.error('Error calculating cart total:', error);
      return 0;
    }
  }
}

export const cartCrudService = new CartCrudService();
